import React, { useEffect } from "react";
import { Button, Col, Row } from "react-bootstrap";
import { isEmpty } from "lodash";
import DividerLine from "./DividerLine";
import "./styles.scss";
import AnswerInput from "./AnswerInput";
import RenderLatexContent from "../RenderLatexContent";
import {
  isNumberExact,
  isNumberLiesInRange,
  isNumberWithPrecision,
} from "../../utils/formater";
import Fa6SolidBookmark from "../../constants/FilledBookmark";
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { setShowEliminateButtons } from "../../store/fullLengthExam";

const optionText = 65;

const ExamOptions = ({
  isSecondHalf = false,
  question,
  index,
  selectedOption,
  handleAnswerSelect,
  markedForLater,
  toggleMarkQuestion,
  onOptionEliminate,
  eliminatedOptions,
  resetEliminatedOptions, // New prop to reset eliminated options
  setNumericAnswer = () => {},
  numericAnswer,
}) => {
  const fullLengthExam = useSelector((state) => state.fullLengthExam);
  const { showEliminateButtons } = fullLengthExam;
  const dispatch = useDispatch();

  const handleToggleEliminateButtons = () => {
    dispatch(setShowEliminateButtons(!showEliminateButtons));
  };

  // Reset eliminated options when moving to the next question
  useEffect(() => {
    resetEliminatedOptions();
    if (question?.type === "numerical") {
      if (!isEmpty(selectedOption)) setNumericAnswer(selectedOption);
      else setNumericAnswer("");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [index]);

  const handleNumericInput = (e) => {
    let input = e.target.value.trim(); // Trim leading and trailing spaces
    input = input.replace(/[^0-9./-]/g, ""); // Allow only digits, '/', '.', and '-' characters
    if (input[0] === "-" && input.length > 6) {
      setNumericAnswer(numericAnswer);
      return;
    } else if (input[0] !== "-" && input.length > 5) {
      setNumericAnswer(numericAnswer);
      return;
    }
    const isCorrect = validateInput(input, question?.options);
    setNumericAnswer(input);
    handleAnswerSelect(question, input, isCorrect);
  };

  function validateInput(userInput, correctAnswer) {
    let check = false;
    const index = correctAnswer?.findIndex(
      (option) => typeof option === "object"
    );
    let option = "";
    if (index !== -1) {
      option = correctAnswer[index];
    } else {
      const stringInd = correctAnswer?.findIndex(
        (option) => typeof option === "string"
      );
      option = {
        type: "Exact Answer:",
        value: stringInd === -1 ? "" : correctAnswer[stringInd],
        errorMargin: "0",
      };
    }
    if (option?.type === "Exact Answer:") {
      check = isNumberExact(userInput, question, option);
      if (!check) {
        // Check if correctAnswer is a valid string
        const givenCorrectAnswer = correctAnswer[0]?.value;
        if (typeof givenCorrectAnswer !== "string") {
          console.error(
            "Correct answer is not a valid string:",
            givenCorrectAnswer
          );
          return false;
        }

        // Split the correct answer on "/" (if it's a fraction)
        const [correctNumeratorString, correctDenominatorString] =
          givenCorrectAnswer.split("/");

        // Convert correct answer to numerical values (handling potential parsing errors)
        const correctNumerator = parseFloat(correctNumeratorString) || 0;
        const correctDenominator = parseFloat(correctDenominatorString) || 1;

        // Check if user input is a fraction
        if (userInput.includes("/")) {
          const [userNumeratorString, userDenominatorString] =
            userInput.split("/");

          // Check if both numerator and denominator are valid (not empty strings)
          if (userNumeratorString !== "" && userDenominatorString !== "") {
            const userNumerator = parseFloat(userNumeratorString) || 0;
            const userDenominator = parseFloat(userDenominatorString) || 1;

            // Accept the answer if numerators and denominators match proportionally
            return (
              Math.abs(userNumerator * correctDenominator) ===
              Math.abs(correctNumerator * userDenominator)
            );
          } else {
            return false; // Invalid fraction format
          }
        } else {
          // Check for decimal representations of the fraction (within a tolerance)
          const tolerance = 0.0001; // Adjust tolerance as needed
          const decimalEquivalent = correctNumerator / correctDenominator;
          return (
            Math.abs(parseFloat(userInput) - decimalEquivalent) <= tolerance ||
            userInput === givenCorrectAnswer
          );
        }
      } else {
        return check;
      }
    }
    if (option?.type === "Answer in the Range:") {
      return isNumberLiesInRange(option?.minValue, option?.maxValue, userInput);
    }
    if (option?.type === "Answer with Precision:") {
      return isNumberWithPrecision(userInput, option?.precision, option?.value);
    }
    return false;
  }

  return (
    <div
      id="option"
      className="exam-options-container"
      style={{
        margin: "5px",
        width: "100%",
        overflow: "hidden",
        paddingTop: isSecondHalf ? "87px" : "",
      }}
    >
      <div className="d-flex">
        <div
          style={{
            background: "black",
            color: "white",
            width: "30px",
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            fontWeight: "bold",
          }}
        >
          <span>{index + 1}</span>
        </div>
        <div className="w-100">
          <Button
            style={{
              fontSize: "16px",
              width: "100%",
              backgroundColor: "rgba(240, 240, 240, 1)",
              color: "black",
              display: "flex",
              justifyContent: "left",
              alignItems: "center",
              border: "none",
            }}
            onClick={() => toggleMarkQuestion(question._id)}
            variant="outline-secondary"
          >
            {markedForLater ? (
              <>
                <span
                  style={{
                    color: "rgb(211, 47, 47)",
                    height: "27px",
                    width: "26px",
                  }}
                >
                  <Fa6SolidBookmark size={20} />
                </span>
                <span>Unmark</span>
              </>
            ) : (
              <>
                <span style={{ color: "black" }}>
                  <img src="/EmptyBookmark.svg" alt="bookmark" height={26} />
                </span>
                <span>Mark for Review</span>
              </>
            )}
          </Button>
        </div>
        <button
          className="eliminate-button"
          aria-label="Eliminate option"
          onClick={handleToggleEliminateButtons}
          style={{ marginLeft: "5px" }}
        >
          <span
            style={{
              backgroundColor: showEliminateButtons ? "blue" : "",
              color: showEliminateButtons ? "white" : "",
              textDecoration: "line-through",
            }}
            className="option-index"
          >
            S
          </span>
        </button>
      </div>
      <div style={{ width: "100vw", marginTop: "5px", marginBottom: "20px" }}>
        <DividerLine />
      </div>
      {isSecondHalf && (
        <Row className="p-0">
          <Col>
            {/* <FormattedText text={question?.statement} className="py-2 px-0" /> */}
            <RenderLatexContent content={question?.statement} />
          </Col>
        </Row>
      )}
      {question?.type === "numerical" ? (
        <div style={{ marginTop: "20px" }}>
          <AnswerInput
            setInputValue={setNumericAnswer}
            inputValue={numericAnswer}
            handleInputChange={handleNumericInput}
          />
        </div>
      ) : (
        <div style={{ marginTop: "20px" }}>
          {question?.options?.map((option, idx) => {
            const isCorrect = idx === question.answer_index;
            const isEliminated = eliminatedOptions?.includes(option);
            return (
              <div
                key={idx}
                style={{ marginTop: idx !== 0 ? "15px" : "" }}
                className={`options-main-div ${
                  isEliminated ? "eliminated" : ""
                }`}
              >
                <div
                  onClick={() =>
                    !isEliminated &&
                    handleAnswerSelect(question, option, isCorrect)
                  }
                  className={`option ${isEliminated ? "eliminated" : ""}`}
                  style={{
                    border:
                      selectedOption === option
                        ? "2px solid blue"
                        : "1px solid grey",
                    background: selectedOption === option ? "#e8f0fe" : "",
                  }}
                >
                  <span
                    className="option-index"
                    style={{
                      backgroundColor: selectedOption === option ? "blue" : "",
                      color: selectedOption === option ? "white" : "",
                      fontWeight: selectedOption === option ? 700 : "",
                    }}
                  >
                    {String.fromCharCode(optionText + idx)}
                  </span>
                  {/* <FormattedText text={option} className="py-2 px-0" /> */}
                  <RenderLatexContent content={option} />
                </div>
                {isEliminated ? (
                  <button
                    onClick={() => onOptionEliminate(option)}
                    className="undo-button"
                    aria-label="Undo eliminate"
                  >
                    Undo
                  </button>
                ) : showEliminateButtons ? (
                  <button
                    onClick={() => onOptionEliminate(option)}
                    className="eliminate-button"
                    aria-label="Eliminate option"
                  >
                    <span className="option-index">
                      {String.fromCharCode(optionText + idx)}
                    </span>
                  </button>
                ) : null}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
};

export default ExamOptions;
